"use client";
import { Post } from "@/axios/AxiosFunctions";
import NoData from "@/components/Core/NoData/NoData";
import { BaseURL, imageUrl } from "@/config/apiUrl";
import { RECORDS_LIMIT } from "@/constant/constants";
import { apiHeader, formatDateTime } from "@/helper/HelperFunction";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { IoChevronDown } from "react-icons/io5";
import { useSelector } from "react-redux";
import Skeleton from "../Skeleton";
import classes from "./CommentSection.module.css";

const LOADING = {
  GET_COMMENTS: "get_comments",
  LOAD_MORE_COMMENTS: "load_more_comments",
  GET_REPLIES: "get_replies",
  LOAD_MORE_REPLIES: "load_more_replies",
};

const CommentSection = ({ postId }) => {
  const { access_token } = useSelector((state) => state.authReducer);
  const [comments, setComments] = useState([]);
  const [totalComments, setTotalComments] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    if (postId) {
      getComments();
    }
  }, []);
  const getComments = async (
    pgNo = page,
    prev = [],
    loadingType = LOADING.GET_COMMENTS
  ) => {
    setLoading(loadingType);
    const url = BaseURL(
      `admin/post-comments/find/${postId}?page=${pgNo}&limit=${RECORDS_LIMIT}`
    );
    const response = await Post(url, {}, apiHeader(access_token));
    if (response !== undefined) {
      setComments([...prev, ...response.data?.comments]);
      setTotalComments(response.data?.totalCount);
    }
    setLoading(null);
  };

  return (
    <div className={classes.commentSection}>
      <div className={classes.commentsList}>
        {loading === LOADING.GET_COMMENTS ? (
          <div className={classes.loadingContainer}>
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton height={40} width="100%" key={index} />
            ))}
          </div>
        ) : comments.length === 0 ? (
          <NoData text="No comments yet" />
        ) : (
          <>
            {comments.map((comment, index) => (
              <CommentItem key={comment._id} comment={comment} />
            ))}
          </>
        )}
      </div>
      {page < Math.ceil(totalComments / RECORDS_LIMIT) && (
        <div className={classes.loadMoreCommentsContainer}>
          {loading == LOADING.LOAD_MORE_COMMENTS ? (
            <Spinner animation="border" />
          ) : (
            <button
              className={classes.loadMoreButton}
              onClick={() => {
                setPage((pre) => pre + 1);
                getComments(page + 1, comments, LOADING.LOAD_MORE_COMMENTS);
              }}
              label={"Show More"}
            >
              <IoChevronDown size={16} />
              Load More
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const CommentItem = ({ comment }) => {
  const { access_token } = useSelector((state) => state.authReducer);
  const [replies, setReplies] = useState([]);
  const [replyPage, setReplyPage] = useState(1);
  const [replyTotalCount, setReplyTotalCount] = useState(0);
  const [replyLoading, setReplyLoading] = useState(null);
  const [toggleReplies, setToggleReplies] = useState(false);

  //   handle toggle replies
  const handleToggleReplies = async () => {
    setToggleReplies((prev) => !prev);
    if (toggleReplies) {
      setReplies([]);
    } else {
      await handleGetReplies();
    }
  };
  //   handle get replies
  const handleGetReplies = async (
    pgNo = replyPage,
    prev = [],
    loadingType = LOADING.GET_REPLIES
  ) => {
    const url = BaseURL(
      `admin/post-comments/find/replies/${comment?._id}?page=${pgNo}&limit=${RECORDS_LIMIT}`
    );
    setReplyLoading(loadingType);
    const response = await Post(url, {}, apiHeader(access_token));
    if (response !== undefined) {
      setReplies([...prev, ...response?.data?.replies]);
      setReplyTotalCount(response?.data?.totalCount);
    }
    setReplyLoading(null);
  };

  return (
    <div className={classes.commentItem}>
      <div className={classes.commentContent}>
        <div className={classes.userAvatarContainer}>
          <div className={classes.userAvatarWrapper}>
            <img
              src={
                comment?.user?.image
                  ? imageUrl(comment?.user?.image)
                  : "/images/user_image.png"
              }
              alt={comment?.user?.fullName}
              className={classes.userAvatar}
            />
          </div>
        </div>
        <div className={classes.userDetails}>
          <div className={classes.userDetailsContent}>
            <h6>{comment?.user?.fullName || "Anonymous"}</h6>
            <p className={classes.commentTime}>
              {formatDateTime(comment?.createdAt)}
            </p>
          </div>
        </div>
        <div className={classes.commentTextContainer}>
          <p className={classes.commentText}>{comment?.text}</p>

          <div className={classes.commentActions}>
            <button className={classes.actionButton}>
              <span className={classes.actionText}>Likes</span>
              {comment?.likeCount > 0 && (
                <span className={classes.countText}>
                  · {comment?.likeCount || 0}
                </span>
              )}
            </button>
            <div className={classes.actionSeparator}>|</div>
            <button className={classes.actionButton}>
              <span className={classes.actionText}>Reply</span>
            </button>
            {comment?.replyCount > 0 && (
              <button
                className={classes.actionButton}
                onClick={handleToggleReplies}
              >
                <span className={classes.actionText}>
                  {toggleReplies
                    ? " · Hide replies"
                    : ` · ${comment?.replyCount} replies`}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      {toggleReplies && (
        <>
          {replyLoading === LOADING.GET_REPLIES ? (
            <div className={classes.loadingContainer}>
              {Array.from({ length: 3 }).map((_, index) => (
                <Skeleton height={40} width="100%" key={index} />
              ))}
            </div>
          ) : (
            <>
              {replies.length > 0 && (
                <div className={classes.repliesContainer}>
                  {replies.map((reply) => (
                    <ReplyItem key={reply?._id} reply={reply} />
                  ))}
                </div>
              )}
              {replyPage < Math.ceil(replyTotalCount / RECORDS_LIMIT) && (
                <div className={classes.loadMoreCommentsContainer}>
                  {replyLoading == LOADING.LOAD_MORE_REPLIES ? (
                    <Spinner animation="border" />
                  ) : (
                    <button
                      className={classes.loadMoreButton}
                      onClick={() => {
                        setReplyPage((pre) => pre + 1);
                        handleGetReplies(
                          replyPage + 1,
                          replies,
                          LOADING.LOAD_MORE_REPLIES
                        );
                      }}
                    >
                      <IoChevronDown size={16} />
                      Load More
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

const ReplyItem = ({ reply }) => {
  return (
    <>
      <div className={classes.replyContent}>
        <div className={classes.userAvatarContainer}>
          <div className={classes.userAvatarWrapper}>
            <img
              src={
                reply?.user?.image
                  ? imageUrl(reply?.user?.image)
                  : "/images/user_image.png"
              }
              alt={reply?.user?.fullName}
              className={classes.userAvatar}
            />
          </div>
        </div>
        <div className={classes.userDetails}>
          <div className={classes.userDetailsContent}>
            <h6>{reply?.user?.fullName || "Anonymous"}</h6>
            <p className={classes.commentTime}>
              {formatDateTime(reply?.createdAt)}
            </p>
          </div>
          {/* show edit and delete button */}
        </div>
        <div className={classes.replyTextContainer}>
          <p className={classes.replyText}>{reply?.text}</p>
          <div className={classes.replyActions}>
            <button className={classes.actionButton}>
              <span className={classes.actionText}>Likes</span>
              {reply?.likeCount > 0 && (
                <span className={classes.countText}> · {reply?.likeCount}</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CommentSection;
